import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react';
import { useApprovedLeaves, useEmployees, useQueryClient, useRosterBalances, useShiftLocations, useShifts, useStaffingTargets, type LeaveRequest } from '../api/hooks';
import { RosterPlanner } from '../components/RosterPlanner';
import { apiPut } from '../api/client';
import type { Employee, Shift, ShiftLocation, ShiftSegment } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { useLang } from '../i18n/LangProvider';
import { toast } from '../ui/toast';
import { Icon } from '../ui/Icon';

const LEAVE_ABBR: Record<string, string> = { ferie: 'FERIE', permesso: 'PERMESSO', malattia: 'MALATTIA' };
const LEAVE_LABEL: Record<string, string> = { ferie: 'Ferie', permesso: 'Permesso', malattia: 'Malattia' };
const LEAVE_COLOR: Record<string, string> = { ferie: '#2b7bb9', permesso: '#e08a1e', malattia: '#c0392b' };

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const STATES = [
  { key: 'off', label: 'OFF' },
  { key: 'standby', label: 'STAND-BY' },
  { key: 'ferie', label: 'FERIE' },
  { key: 'malattia', label: 'MALATTIA' },
  { key: 'permesso', label: 'PERMESSO' },
] as const;
const STATE_KEYS = new Set<string>(STATES.map((s) => s.key));
const STATE_YELLOW = '#ffe74d';
const LOC_COLOR: Record<string, string> = {
  uff: '#e79aa6', vat: '#93cf8f', col: '#5bb8e6', nav: '#f2a63d', home: '#c3ace0', csam: '#9ad0c0', borgh: '#e6c34a',
};
const locColor = (key: string) => LOC_COLOR[key] ?? '#c9c9c9';

function mondayOf(d: Date): Date {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
}
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6;

// The date row is frozen, so its background has to be fully opaque or the cells
// scroll through it. Today's tint is therefore layered over the header colour
// rather than blended into it with alpha — same result on screen, no see-through.
const HEAD_BG = 'var(--bg-sunk,#f6f6f6)';
const HEAD_BG_TODAY = `linear-gradient(rgba(238,100,32,.12), rgba(238,100,32,.12)), ${HEAD_BG}`;

/** Phone layout switch — mirrors the 768px breakpoint used across the stylesheet. */
function useIsNarrow() {
  const query = '(max-width: 768px)';
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

/** A cell is a full-day state, or 1 segment (whole day) / 2 segments (split). */
type CellValue = { state?: string; segments: ShiftSegment[] } | null;
const hasLoc = (s?: ShiftSegment) => !!s?.loc;

/** Assumed length when a day records a start but no end — matches the API. */
const FALLBACK_SHIFT_HOURS = 8;
/** Used until someone fills in a contract — mirrors the API default. */
const DEFAULT_WEEKLY_HOURS = 40;
const minutesOf = (hhmm?: string | null) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((hhmm ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  return h > 23 || min > 59 ? null : h * 60 + min;
};

/**
 * Hours one cell is worth. Mirrors the server's rule (apps/api roster.hours):
 * the first segment holds Inizio and the second Fine, so a day is last − first.
 * Computed locally so the warning follows the pencil, before anything is saved.
 */
function cellHours(v: CellValue): number {
  if (!v || v.state || !v.segments.some(hasLoc)) return 0;
  const times = v.segments.map((s) => minutesOf(s.range)).filter((n): n is number => n !== null);
  if (times.length < 2) return FALLBACK_SHIFT_HOURS;
  const span = times[times.length - 1] - times[0];
  return Math.round(((span > 0 ? span : span + 24 * 60) / 60) * 100) / 100;
}

function readShift(s: Shift | undefined): CellValue {
  if (!s) return { segments: [] };
  if (s.typeKey && STATE_KEYS.has(s.typeKey)) return { state: s.typeKey, segments: [] };
  const raw = (s.segments && s.segments.length ? s.segments : (s.locationKey ? [{ loc: s.locationKey, range: s.range }] : [])).slice(0, 2);
  return { segments: raw.some(hasLoc) ? raw : [] };
}
const isEmpty = (v: CellValue) => !v || (!v.state && !v.segments.some(hasLoc));
function payloadFor(employeeId: string, date: string, v: CellValue) {
  if (isEmpty(v)) return { employeeId, date, typeKey: null, locationKey: null, range: null, segments: [] };
  if (v!.state) return { employeeId, date, typeKey: v!.state, locationKey: null, range: null, segments: [] };
  return { employeeId, date, typeKey: 'work', locationKey: v!.segments[0]?.loc || null, range: v!.segments[0]?.range || null, segments: v!.segments };
}

type Armed = { kind: 'loc'; loc: string } | { kind: 'state'; key: string } | { kind: 'clear' } | null;

export function ShiftsScreen() {
  const { lang, t } = useLang();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const qc = useQueryClient();
  const auth = useAuth();
  const employeesQ = useEmployees();
  const shiftsQ = useShifts();
  const locationsQ = useShiftLocations();

  // Four ways to look at the roster: the editing grid (to build turni), a
  // per-person agenda, a per-day "chi c'è" board — the middle two are read-only
  // and much friendlier on a phone — and the planning desk behind the
  // auto-generator. The choice is remembered across visits.
  const [view, setView] = useState<'grid' | 'person' | 'day' | 'auto'>(() => {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem('shifts.view') : null;
    return v === 'person' || v === 'day' || v === 'auto' ? v : 'grid';
  });
  useEffect(() => { try { localStorage.setItem('shifts.view', view); } catch { /* ignore */ } }, [view]);
  const [personId, setPersonId] = useState('');
  const [dayCursor, setDayCursor] = useState<Date>(startOfToday);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [weeks, setWeeks] = useState(1);
  const [time, setTime] = useState('09:00');
  const [time2, setTime2] = useState('17:00');
  const [armed, setArmed] = useState<Armed>(null);
  const [sel, setSel] = useState<{ ar: number; ac: number; fr: number; fc: number } | null>(null);
  // Clipboard holds a 2D block so a whole selected area can be copied and pasted.
  const [clipboard, setClipboard] = useState<CellValue[][] | null>(null);
  const [editor, setEditor] = useState<{ r: number; c: number } | null>(null);
  const [local, setLocal] = useState<Record<string, CellValue>>({});
  const [saving, setSaving] = useState(0);
  const narrow = useIsNarrow();
  const mode = useRef<null | 'paint' | 'select'>(null);
  const pending = useRef<Map<string, { employeeId: string; date: string; value: CellValue }>>(new Map());
  const rootRef = useRef<HTMLDivElement>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const employees = (employeesQ.data?.items ?? []).filter((e) => e.active);
  // Default the agenda to the logged-in person when they are on the roster.
  const meId = employees.some((e) => e.id === auth.sub) ? auth.sub : '';
  const activePersonId = personId && employees.some((e) => e.id === personId) ? personId : (meId || employees[0]?.id || '');
  const locations: ShiftLocation[] = locationsQ.data ?? [];
  const locLabel = (key?: string | null) => locations.find((l) => l.key === key)?.short ?? key?.toUpperCase() ?? '';
  const days = useMemo(
    () => Array.from({ length: 7 * weeks }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; }),
    [weekStart, weeks],
  );
  const lastCol = days.length - 1;
  const cellKey = (r: number, c: number) => `${employees[r]?.id}|${iso(days[c])}`;

  // Approved leaves overlaid on the roster (full-day marker or partial-hours badge).
  const leavesQ = useApprovedLeaves(iso(days[0]), iso(days[lastCol]));
  // Contracts and staffing needs: the two things the grid warns against.
  const balancesQ = useRosterBalances(iso(days[0]), iso(days[lastCol]));
  const targetsQ = useStaffingTargets();
  const leavesByKey = useMemo(() => {
    const map: Record<string, LeaveRequest> = {};
    for (const l of leavesQ.data ?? []) {
      const s = new Date(`${l.startDate.slice(0, 10)}T00:00:00`);
      const e = new Date(`${l.endDate.slice(0, 10)}T00:00:00`);
      for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) map[`${l.employeeId}|${iso(d)}`] = l;
    }
    return map;
  }, [leavesQ.data]);

  const serverMap = useMemo(() => {
    const m: Record<string, Shift> = {};
    for (const s of shiftsQ.data ?? []) m[`${s.employeeId}|${s.date.slice(0, 10)}`] = s;
    return m;
  }, [shiftsQ.data]);
  const getCell = (r: number, c: number): CellValue => {
    const k = cellKey(r, c);
    return k in local ? local[k] : readShift(serverMap[k]);
  };

  const write = (r: number, c: number, value: CellValue) => {
    const k = cellKey(r, c);
    setLocal((l) => ({ ...l, [k]: value }));
    pending.current.set(k, { employeeId: employees[r].id, date: iso(days[c]), value });
  };
  const flush = useCallback(async () => {
    if (pending.current.size === 0) return;
    const batch = [...pending.current.entries()];
    pending.current.clear();
    setSaving((n) => n + 1);
    const results = await Promise.all(
      batch.map(([, p]) => apiPut('/shifts', payloadFor(p.employeeId, p.date, p.value)).then(() => true, () => false)),
    );
    setSaving((n) => n - 1);
    // A silent failure would leave the grid showing a turno the server never got.
    const failed = results.filter((ok) => !ok).length;
    if (failed) toast.error(failed === 1 ? t('Una modifica non è stata salvata.') : t('{n} modifiche non sono state salvate.', { n: failed }));
    await qc.invalidateQueries({ queryKey: ['shifts'] });
    void qc.invalidateQueries({ queryKey: ['roster-balances'] });
    setLocal((l) => { const n = { ...l }; for (const [k] of batch) delete n[k]; return n; });
  }, [qc, t]);

  /** Applies the armed value. half 1 = whole day, half 2 = only the second part. */
  const applyAt = (r: number, c: number, half: 1 | 2) => {
    if (!armed) return;
    if (armed.kind === 'clear') { write(r, c, null); return; }
    if (armed.kind === 'state') { write(r, c, { state: armed.key, segments: [] }); return; }
    if (half === 1) {
      // Whole day: both halves at the same location, showing start and end time.
      write(r, c, { segments: [{ loc: armed.loc, range: time || undefined }, { loc: armed.loc, range: time2 || undefined }] });
    } else {
      const cur = getCell(r, c);
      const first = cur && !cur.state && hasLoc(cur.segments[0]) ? cur.segments[0] : { loc: '' };
      write(r, c, { segments: [first, { loc: armed.loc, range: time2 || undefined }] });
    }
  };

  // ── selection (used when nothing is armed: copy / paste / clear) ──
  const rect = sel ? { r0: Math.min(sel.ar, sel.fr), r1: Math.max(sel.ar, sel.fr), c0: Math.min(sel.ac, sel.fc), c1: Math.max(sel.ac, sel.fc) } : null;
  const inSel = (r: number, c: number) => !!rect && r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1;
  const eachSel = (fn: (r: number, c: number) => void) => { if (!rect) return; for (let r = rect.r0; r <= rect.r1; r++) for (let c = rect.c0; c <= rect.c1; c++) fn(r, c); };
  const selCount = rect ? (rect.r1 - rect.r0 + 1) * (rect.c1 - rect.c0 + 1) : 0;

  // ── block copy / paste / clear (work by button too, no keyboard needed) ──
  const copyBlock = () => {
    if (!rect) return;
    const block: CellValue[][] = [];
    for (let r = rect.r0; r <= rect.r1; r++) {
      const row: CellValue[] = [];
      for (let c = rect.c0; c <= rect.c1; c++) row.push(getCell(r, c));
      block.push(row);
    }
    setClipboard(block);
  };
  const pasteBlock = () => {
    if (!rect || !clipboard) return;
    // Anchor the block at the selection's top-left; tile to fill a larger selection.
    const bh = clipboard.length, bw = clipboard[0]?.length ?? 0;
    if (!bh || !bw) return;
    for (let r = rect.r0; r <= rect.r1; r++) {
      for (let c = rect.c0; c <= rect.c1; c++) {
        if (r >= employees.length || c >= days.length) continue;
        write(r, c, clipboard[(r - rect.r0) % bh][(c - rect.c0) % bw]);
      }
    }
    void flush();
  };
  const clearSel = () => { if (!rect) return; eachSel((r, c) => write(r, c, null)); void flush(); };

  // Excel-style frozen panes only hold still if the roster is its own scroll box,
  // so it gets a height: whatever is left below the toolbar. Measured rather than
  // guessed, because the palette grows and shrinks with the armed tool. The
  // document offset (not the viewport one) keeps the value stable even if the
  // page happens to be scrolled when we measure.
  useEffect(() => {
    const el = gridRef.current, root = rootRef.current;
    if (view !== 'grid' || !el || !root) return;
    const sync = () => {
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      root.style.setProperty('--shr-max-h', `${Math.max(240, Math.round(window.innerHeight - docTop - 12))}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    if (paletteRef.current) ro.observe(paletteRef.current);
    window.addEventListener('resize', sync);
    return () => { ro.disconnect(); window.removeEventListener('resize', sync); };
  }, [view, narrow, employees.length]);

  useEffect(() => {
    const up = () => { if (mode.current === 'paint') void flush(); mode.current = null; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [flush]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') { setArmed(null); return; }
      if (!rect) return;
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'c') { e.preventDefault(); copyBlock(); }
      else if ((e.metaKey || e.ctrlKey) && k === 'v') { e.preventDefault(); pasteBlock(); }
      else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); clearSel(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /** Which half of the cell the pointer is over. */
  const halfOf = (e: { clientX: number; currentTarget: HTMLElement }): 1 | 2 => {
    const b = e.currentTarget.getBoundingClientRect();
    return e.clientX - b.left < b.width / 2 ? 1 : 2;
  };

  // ── touch: distinguish a tap (fill / select) from a scroll drag ──
  const touchStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const onCellTouchStart = (e: ReactTouchEvent) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };
  const onCellTouchEnd = (r: number, c: number, half: 1 | 2, e: ReactTouchEvent) => {
    const s = touchStart.current; touchStart.current = null;
    if (!s) return;
    const touch = e.changedTouches[0];
    const moved = Math.hypot(touch.clientX - s.x, touch.clientY - s.y);
    if (moved > 12) return; // a scroll, not a tap
    // Long-press stands in for the desktop double-click: it opens the editor.
    if (Date.now() - s.t > 500) { e.preventDefault(); setEditor({ r, c }); return; }
    e.preventDefault(); // suppress the synthesized mouse events
    if (armed) { applyAt(r, c, half); void flush(); return; }
    // No tool armed → build a rectangular selection by tapping corners.
    setSel((prev) => {
      const single = prev && prev.ar === prev.fr && prev.ac === prev.fc;
      if (single && prev!.ar === r && prev!.ac === c) return null;          // tap same cell → deselect
      if (single) return { ar: prev!.ar, ac: prev!.ac, fr: r, fc: c };      // extend to a rectangle
      return { ar: r, ac: c, fr: r, fc: c };                                 // start a new selection
    });
  };

  const applyRowOrCol = (cells: [number, number][]) => {
    if (!armed) return false;
    cells.forEach(([r, c]) => applyAt(r, c, 1));
    void flush();
    return true;
  };

  const todayIso = iso(new Date());
  const weekLabel = `${weekStart.toLocaleDateString(loc, { day: 'numeric', month: 'short' })} – ${days[lastCol].toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  const dayLabel = dayCursor.toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'short' });
  // Phones get tighter columns and a much narrower name column so at least
  // three days stay on screen next to the frozen names.
  const colMin = narrow ? (weeks === 1 ? 96 : weeks === 2 ? 84 : 72) : weeks === 1 ? 120 : weeks === 2 ? 96 : 78;
  const nameW = narrow ? 92 : 170;
  const chip = (bg: string, on: boolean) => ({
    background: bg, color: '#222', fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '5px 10px',
    border: on ? '2px solid #222' : '2px solid transparent', cursor: 'pointer',
    boxShadow: on ? '0 0 0 3px rgba(0,0,0,.08)' : undefined,
  } as const);
  const armedLabel = !armed ? '' : armed.kind === 'loc' ? locLabel(armed.loc)
    : armed.kind === 'state' ? t(STATES.find((s) => s.key === armed.key)?.label ?? '') : t('Svuota');

  /**
   * Planned hours per person, week by week, straight from what is on screen —
   * unsaved edits included, so the contract warning moves with the pencil.
   * The server owns the contract itself; the arithmetic stays local.
   */
  const contractOf = (id: string) =>
    balancesQ.data?.find((b) => b.employeeId === id)?.weeklyHours ?? DEFAULT_WEEKLY_HOURS;
  const hoursByRow = useMemo(() => {
    return employees.map((emp, r) => {
      const byWeek = new Map<string, number>();
      days.forEach((d, c) => {
        const k = `${emp.id}|${iso(d)}`;
        const v = k in local ? local[k] : readShift(serverMap[k]);
        const wk = iso(mondayOf(d));
        byWeek.set(wk, (byWeek.get(wk) ?? 0) + cellHours(v));
      });
      const contract = contractOf(emp.id);
      const total = [...byWeek.values()].reduce((n, h) => n + h, 0);
      const worstWeek = Math.max(0, ...byWeek.values());
      return { r, id: emp.id, name: emp.name, total: Math.round(total * 10) / 10, contract, over: worstWeek > contract, worstWeek: Math.round(worstWeek * 10) / 10 };
    });
    // `local` and the server data are what actually move these numbers.
  }, [employees, days, local, serverMap, balancesQ.data]);
  const overRows = hoursByRow.filter((h) => h.over);

  /**
   * Coverage against the staffing targets, day by day. Computed from what is on
   * screen so a day that drops below its required headcount says so while the
   * roster is being built, not only after the generator runs.
   */
  const coverage = useMemo(() => {
    const want = new Map<string, number>();
    for (const tg of targetsQ.data ?? []) want.set(`${tg.locationKey}|${tg.weekday}`, tg.count);
    return days.map((d) => {
      const weekday = (d.getDay() + 6) % 7;
      const have = new Map<string, number>();
      for (const emp of employees) {
        const k = `${emp.id}|${iso(d)}`;
        const v = k in local ? local[k] : readShift(serverMap[k]);
        if (!v || v.state) continue;
        // Someone split across two sedi counts once at each of them.
        for (const l of new Set(v.segments.filter(hasLoc).map((s) => s.loc as string))) {
          have.set(l, (have.get(l) ?? 0) + 1);
        }
      }
      const short = [...want.entries()]
        .filter(([key, count]) => count > 0 && key.endsWith(`|${weekday}`))
        .map(([key, count]) => ({ loc: key.slice(0, key.lastIndexOf('|')), want: count, got: have.get(key.slice(0, key.lastIndexOf('|'))) ?? 0 }))
        .filter((x) => x.got < x.want);
      return { date: d, short, missing: short.reduce((n, x) => n + (x.want - x.got), 0) };
    });
  }, [days, employees, local, serverMap, targetsQ.data]);
  const understaffed = coverage.filter((c) => c.missing > 0);

  return (
    <div className="shifts-screen" ref={rootRef} style={{ padding: 24 }}>
      <div className="page-head shifts-head" style={{ marginBottom: 10 }}>
        <div className="shifts-head__titles">
          <h1 className="h1">Turni</h1>
          <div className="page-head__sub shifts-head__sub">
            {view === 'grid'
              ? <>{t('Scegli una sede, poi clicca:')} <b>{t('metà sinistra')}</b> = {t('tutto il giorno')}, <b>{t('metà destra')}</b> = {t('solo 2ª parte')}.</>
              : view === 'person'
                ? <>{t('Agenda del singolo addetto, giorno per giorno.')}</>
                : view === 'day'
                  ? <>{t('Chi lavora e dove, raggruppato per sede.')}</>
                  : <>{t('Fabbisogno per sede, preferenze del personale e generazione automatica.')}</>}
          </div>
        </div>
        <div className="shifts-toolbar">
          <div className="role-switch shifts-view-switch">
            <button className={view === 'grid' ? 'is-active' : ''} onClick={() => setView('grid')} title={t('Griglia per creare i turni')}>{t('Griglia')}</button>
            <button className={view === 'person' ? 'is-active' : ''} onClick={() => setView('person')} title={t('Agenda per persona')}>{t('Persona')}</button>
            <button className={view === 'day' ? 'is-active' : ''} onClick={() => setView('day')} title={t("Chi c'è, per giornata")}>{t('Giornata')}</button>
            <button className={view === 'auto' ? 'is-active' : ''} onClick={() => setView('auto')} title={t('Fabbisogno, preferenze e generazione automatica')}>{t('Auto')}</button>
          </div>
          {view === 'day' ? (
            <div className="row shifts-nav" style={{ gap: 6, alignItems: 'center' }}>
              <button className="btn btn--ghost btn--icon" aria-label="Giorno precedente" onClick={() => setDayCursor((d) => { const n = new Date(d); n.setDate(d.getDate() - 1); return n; })}><Icon name="chevL" size={15} /></button>
              <span className="shifts-week-label" style={{ minWidth: 170, textAlign: 'center', fontWeight: 600, fontSize: 14, textTransform: 'capitalize' }}>{dayLabel}</span>
              <button className="btn btn--ghost btn--icon" aria-label="Giorno successivo" onClick={() => setDayCursor((d) => { const n = new Date(d); n.setDate(d.getDate() + 1); return n; })}><Icon name="chevR" size={15} /></button>
              <button className="btn btn--sm" onClick={() => setDayCursor(startOfToday())}>{t('Oggi')}</button>
            </div>
          ) : (
            <div className="row shifts-nav" style={{ gap: 6, alignItems: 'center' }}>
              <div className="role-switch shifts-weeks">
                {[1, 2, 4].map((w) => (
                  <button key={w} className={weeks === w ? 'is-active' : ''} onClick={() => setWeeks(w)}>{w} {t('sett.')}</button>
                ))}
              </div>
              <button className="btn btn--ghost btn--icon" aria-label="Settimana precedente" onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(w.getDate() - 7 * weeks); return n; })}><Icon name="chevL" size={15} /></button>
              <span className="shifts-week-label" style={{ minWidth: 190, textAlign: 'center', fontWeight: 600, fontSize: 14 }}>{weekLabel}</span>
              <button className="btn btn--ghost btn--icon" aria-label="Settimana successiva" onClick={() => setWeekStart((w) => { const n = new Date(w); n.setDate(w.getDate() + 7 * weeks); return n; })}><Icon name="chevR" size={15} /></button>
              <button className="btn btn--sm" onClick={() => setWeekStart(mondayOf(new Date()))}>{t('Oggi')}</button>
            </div>
          )}
        </div>
      </div>

      {view === 'grid' && (
      <>
      <div className="card sh-palette" ref={paletteRef}>
        {/* Tools first: on a phone this is the only row always on screen. */}
        <div className="sh-palette__row sh-palette__tools">
          {locations.map((l) => {
            const on = armed?.kind === 'loc' && armed.loc === l.key;
            return (
              <button key={l.key} onClick={() => setArmed(on ? null : { kind: 'loc', loc: l.key })} style={chip(locColor(l.key), on)} title={l.label}>
                {l.short ?? l.key.toUpperCase()}
              </button>
            );
          })}
          <span className="sh-palette__sep" />
          {STATES.map((s) => {
            const on = armed?.kind === 'state' && armed.key === s.key;
            return <button key={s.key} onClick={() => setArmed(on ? null : { kind: 'state', key: s.key })} style={chip(STATE_YELLOW, on)}>{t(s.label)}</button>;
          })}
          <button onClick={() => setArmed(armed?.kind === 'clear' ? null : { kind: 'clear' })} className="btn btn--sm"
                  style={{ border: armed?.kind === 'clear' ? '2px solid #222' : undefined }}>
            <Icon name="x" size={12} /> {t('Svuota')}
          </button>
        </div>

        {/* Times matter only while a sede is armed, so on a phone they appear then. */}
        <div className={`sh-palette__row sh-palette__times${armed?.kind === 'loc' ? ' is-on' : ''}`}>
          <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{t('Inizio')}</span>
          <input type="time" className="input" style={{ width: 96, padding: '4px 8px', height: 'auto' }} value={time} onChange={(e) => setTime(e.target.value)} title={t('Orario di inizio (1ª metà)')} />
          <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{t('Fine')}</span>
          <input type="time" className="input" style={{ width: 96, padding: '4px 8px', height: 'auto' }} value={time2} onChange={(e) => setTime2(e.target.value)} title={t('Orario di fine (2ª metà)')} />
        </div>

        {/* Selection actions — work by tap on touch, no keyboard needed, and stay
            out of the way on a phone until something is actually selected. */}
        <div className={`sh-palette__row sh-palette__actions${rect ? ' is-on' : ''}`}>
          <button className="btn btn--sm" disabled={!rect} onClick={copyBlock} title={t('Copia la selezione')}><Icon name="copy" size={12} /> {t('Copia')}{selCount > 1 ? ` (${selCount})` : ''}</button>
          <button className="btn btn--sm" disabled={!rect || !clipboard} onClick={pasteBlock} title={t('Incolla nella selezione')}><Icon name="download" size={12} /> {t('Incolla')}</button>
          <button className="btn btn--sm" disabled={!rect} onClick={clearSel} title={t('Svuota le celle selezionate')}><Icon name="trash" size={12} /> {t('Cancella')}</button>
          {rect && <button className="btn btn--sm" onClick={() => setSel(null)}>{t('Deseleziona')}</button>}
        </div>

        <div className="sh-palette__hint">
          <span className="muted sh-palette__hint--wide">
            {armed
              ? <>{t('Attivo:')} <b>{armedLabel}</b> — {t('tocca le celle · tocca nome/giorno per riempire riga/colonna ·')} <b>Esc</b> {t('per disattivare')}</>
              : <>{t('Nessuno strumento attivo: tocca le celle per selezionare un’area (tocca due angoli), poi usa Copia / Incolla / Cancella')}{clipboard ? ` · ${t('blocco copiato')} ${clipboard.length}×${clipboard[0]?.length ?? 0}` : ''}</>}
          </span>
          <span className="muted sh-palette__hint--slim">
            {armed ? <>{t('Attivo:')} <b>{armedLabel}</b></> : rect ? `${selCount} ${t('selezionate')}` : t('Tocca 2 angoli per selezionare · tieni premuto per gli orari')}
          </span>
          <span className="sh-palette__status muted">
            {saving > 0 ? t('Salvataggio…') : !armed && rect ? `${selCount} ${t('selezionate')}` : ''}
          </span>
        </div>
      </div>

      {/* Staffing shortfall: the sedi that do not reach the headcount they asked
          for. Like the contract warning it stays in the page, never a dialog. */}
      {understaffed.length > 0 && (
        <div className="sh-overload is-short" role="status">
          <Icon name="warn" size={14} />
          <span>
            {t('Fabbisogno non coperto in {n} giornate:', { n: understaffed.length })}{' '}
            {understaffed.slice(0, 4).map((c) => (
              `${c.date.getDate()}/${c.date.getMonth() + 1} ${c.short.map((s) => `${locLabel(s.loc)} ${s.got}/${s.want}`).join(', ')}`
            )).join(' · ')}
            {understaffed.length > 4 && ` · +${understaffed.length - 4}`}
          </span>
        </div>
      )}

      {/* Contract warning: a strip above the grid, never a dialog — it must not
          interrupt someone in the middle of painting a week. */}
      {overRows.length > 0 && (
        <div className="sh-overload" role="status">
          <Icon name="warn" size={14} />
          <span>
            {overRows.length === 1
              ? <>{t('Oltre il contratto:')} <b>{overRows[0].name.split(' ')[0]}</b> {overRows[0].worstWeek}h / {overRows[0].contract}h</>
              : <>{t('{n} persone oltre il contratto:', { n: overRows.length })} {overRows.map((o) => `${o.name.split(' ')[0]} ${o.worstWeek}/${o.contract}h`).join(' · ')}</>}
          </span>
        </div>
      )}

      {employees.length === 0 ? (
        <div className="card" style={{ padding: 24 }}><p className="muted" style={{ margin: 0 }}>{t('Nessun dipendente attivo. Aggiungi utenti dalla Dashboard Admin.')}</p></div>
      ) : (
        // A real table, not a CSS grid: `position: sticky` on a grid item is
        // clamped to its own grid area, so the frozen name column only actually
        // holds still on table cells.
        <div className="shr-scroll" ref={gridRef} style={{ userSelect: 'none' }}>
          <table className="shr-table" style={{ minWidth: nameW + days.length * colMin }}>
            <colgroup>
              <col style={{ width: nameW }} />
              {days.map((d) => <col key={iso(d)} />)}
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="shr-corner" onClick={() => { const all: [number, number][] = []; employees.forEach((_, r) => days.forEach((__, c) => all.push([r, c]))); if (!applyRowOrCol(all)) setSel({ ar: 0, ac: 0, fr: employees.length - 1, fc: lastCol }); }}
                    title={t('Tutta la settimana')} style={{ background: HEAD_BG, padding: '8px 10px', fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', borderRadius: 6, cursor: 'pointer' }}>{t('Dipendente')}</th>
                {days.map((d, c) => (
                  <th scope="col" key={iso(d)} className="shr-head" onClick={() => { const col: [number, number][] = employees.map((_, r) => [r, c]); if (!applyRowOrCol(col)) setSel({ ar: 0, ac: c, fr: employees.length - 1, fc: c }); }}
                      title={t('Questa giornata per tutti')}
                      style={{ background: iso(d) === todayIso ? HEAD_BG_TODAY : HEAD_BG, padding: '6px 4px', textAlign: 'center', borderRadius: 6, cursor: 'pointer' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: iso(d) === todayIso ? 'var(--t-orange)' : 'var(--ink-2)' }}>{d.getDate()}-{d.toLocaleDateString(loc, { month: 'short' })}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{d.toLocaleDateString(loc, { weekday: 'long' })}</div>
                    {/* How far this day is from the headcount it needs. */}
                    {coverage[c]?.missing > 0 && (
                      <span className="shr-short" title={coverage[c].short.map((s) => `${locLabel(s.loc)} ${s.got}/${s.want}`).join(' · ')}>
                        −{coverage[c].missing}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp, r) => (
                <tr key={emp.id}>
                  <th scope="row" className="shr-name" onClick={() => { const row: [number, number][] = days.map((_, c) => [r, c]); if (!applyRowOrCol(row)) setSel({ ar: r, ac: 0, fr: r, fc: lastCol }); }}
                      title={`${emp.name} — ${t('Tutta la settimana di questa persona')}`}
                      style={{ background: 'var(--bg,#fff)', padding: narrow ? '6px' : '6px 10px', borderRadius: 6, border: '1px solid var(--line,#eee)', cursor: 'pointer', overflow: 'hidden' }}>
                    <span style={{ display: 'block', fontSize: narrow ? 11.5 : 12.5, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.name.split(' ')[0]}</span>
                    <span style={{ display: 'block', fontSize: narrow ? 10 : 11, color: 'var(--ink-3)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {narrow
                        ? emp.name.split(' ').slice(1).map((w) => w[0]).join('')
                        : emp.name.split(' ').slice(1).join(' ')}
                    </span>
                    {/* Hours against the contract, right where the week is being built. */}
                    <span className={`shr-hours${hoursByRow[r]?.over ? ' is-over' : ''}`}
                          title={t('Ore pianificate nel periodo · contratto {h}h a settimana', { h: hoursByRow[r]?.contract ?? DEFAULT_WEEKLY_HOURS })}>
                      {hoursByRow[r]?.total ?? 0}h<span className="shr-hours__of">/{hoursByRow[r]?.contract ?? DEFAULT_WEEKLY_HOURS}</span>
                    </span>
                  </th>
                  {days.map((d, c) => {
                    const v = getCell(r, c);
                    const selected = !armed && inSel(r, c);
                    const segs = v?.segments ?? [];
                    const raw = leavesByKey[`${emp.id}|${iso(d)}`];
                    // A full-day absence now lands on the roster as the turno itself,
                    // so the strip underneath is only for hourly permits (or a leave
                    // whose day was later overwritten by hand).
                    const leave = raw && (raw.partial || v?.state !== raw.type) ? raw : undefined;
                    return (
                      <td key={`${emp.id}|${iso(d)}`} data-cell={`${r}-${c}`} className="shr-cell"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (armed) { mode.current = 'paint'; applyAt(r, c, halfOf(e)); }
                            else { mode.current = 'select'; setSel((s) => (e.shiftKey && s ? { ...s, fr: r, fc: c } : { ar: r, ac: c, fr: r, fc: c })); }
                          }}
                          onMouseEnter={(e) => {
                            if (mode.current === 'paint' && armed) applyAt(r, c, halfOf(e));
                            else if (mode.current === 'select') setSel((s) => (s ? { ...s, fr: r, fc: c } : s));
                          }}
                          onTouchStart={onCellTouchStart}
                          onTouchEnd={(e) => onCellTouchEnd(r, c, e.changedTouches[0] && (e.changedTouches[0].clientX - e.currentTarget.getBoundingClientRect().left) < e.currentTarget.getBoundingClientRect().width / 2 ? 1 : 2, e)}
                          onDoubleClick={() => setEditor({ r, c })}
                          title={armed ? t('Sinistra = tutto il giorno · destra = 2ª parte') : t('Clic per selezionare · doppio clic per gli orari')}
                          style={{ border: selected ? '2px solid var(--t-orange)' : '1px solid var(--line,#eee)', borderRadius: 6, overflow: 'hidden', cursor: armed ? 'pointer' : 'cell', background: 'var(--bg,#fff)', height: 46, padding: 0, position: 'relative' }}>
                        {/* Shift content fills the top; a leave marker gets its own strip below so it never covers the office. */}
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
                            {v?.state ? (
                              <span style={{ flex: 1, background: STATE_YELLOW, color: '#5a4a00', fontWeight: 700, fontSize: 12, display: 'grid', placeItems: 'center' }}>
                                {STATES.find((s) => s.key === v.state)?.label ?? v.state.toUpperCase()}
                              </span>
                            ) : segs.some(hasLoc) ? (
                              segs.map((seg, i) => (
                                hasLoc(seg) ? (
                                  <span key={i} style={{ flex: 1, background: locColor(seg.loc ?? ''), color: '#222', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderLeft: i ? '1px solid rgba(0,0,0,.08)' : undefined }}>
                                    <span style={{ fontWeight: 700, fontSize: 11.5 }}>{locLabel(seg.loc)}</span>
                                    {seg.range && <span style={{ fontSize: 10.5 }}>{seg.range}</span>}
                                  </span>
                                ) : <span key={i} style={{ flex: 1 }} />
                              ))
                            ) : (
                              <span style={{ flex: 1, color: 'var(--ink-4)', fontSize: 15, display: 'grid', placeItems: 'center' }}>+</span>
                            )}
                            {/* hint of the two halves while a value is armed */}
                            {armed && <span style={{ position: 'absolute', inset: 0, pointerEvents: 'none', borderLeft: '0', background: 'linear-gradient(to right, transparent 49.6%, rgba(0,0,0,.14) 49.6%, rgba(0,0,0,.14) 50.4%, transparent 50.4%)' }} />}
                          </div>
                          {/* Approved leave strip below the shift — clearly labelled, never overlapping. */}
                          {leave && (
                            <span title={leave.partial ? `Permesso ${leave.startTime}–${leave.endTime}` : LEAVE_LABEL[leave.type]}
                              style={{ background: LEAVE_COLOR[leave.type], color: '#fff', fontSize: 9, fontWeight: 700, textAlign: 'center', letterSpacing: '.02em', pointerEvents: 'none', padding: '1px 2px', lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {leave.partial ? `PERM ${leave.startTime}–${leave.endTime}` : LEAVE_ABBR[leave.type]}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>
      )}

      {view === 'person' && (
        <PersonAgenda days={days} employees={employees} locations={locations} personId={activePersonId} setPersonId={setPersonId} meId={meId} />
      )}
      {view === 'day' && (
        <DayRoster date={dayCursor} employees={employees} locations={locations} />
      )}
      {view === 'auto' && (
        <RosterPlanner locations={locations} employees={employees} from={iso(days[0])} to={iso(days[lastCol])} />
      )}

      {editor && employees[editor.r] && (
        <ShiftEditor
          name={employees[editor.r].name}
          date={iso(days[editor.c])}
          current={getCell(editor.r, editor.c)}
          locations={locations}
          defaultTime={time}
          onClose={() => setEditor(null)}
          onSave={(v) => { write(editor.r, editor.c, v); void flush(); setEditor(null); }}
        />
      )}
    </div>
  );
}

/** Read-only agenda for one person: the week (or 2/4 weeks) as a vertical list. */
function PersonAgenda({ days, employees, locations, personId, setPersonId, meId }: {
  days: Date[]; employees: Employee[]; locations: ShiftLocation[]; personId: string; setPersonId: (id: string) => void; meId: string;
}) {
  const { lang, t } = useLang();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const shiftsQ = useShifts();
  const leavesQ = useApprovedLeaves(iso(days[0]), iso(days[days.length - 1]));
  const locLabel = (key?: string | null) => locations.find((l) => l.key === key)?.short ?? key?.toUpperCase() ?? '';
  const todayIso = iso(new Date());

  const byDate = useMemo(() => {
    const m: Record<string, Shift> = {};
    for (const s of shiftsQ.data ?? []) if (s.employeeId === personId) m[s.date.slice(0, 10)] = s;
    return m;
  }, [shiftsQ.data, personId]);

  const leaveByDate = useMemo(() => {
    const map: Record<string, LeaveRequest> = {};
    for (const l of leavesQ.data ?? []) {
      if (l.employeeId !== personId) continue;
      const s = new Date(`${l.startDate.slice(0, 10)}T00:00:00`);
      const e = new Date(`${l.endDate.slice(0, 10)}T00:00:00`);
      for (const d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) map[iso(d)] = l;
    }
    return map;
  }, [leavesQ.data, personId]);

  if (employees.length === 0) {
    return <div className="card" style={{ padding: 24 }}><p className="muted" style={{ margin: 0 }}>{t('Nessun dipendente attivo.')}</p></div>;
  }

  return (
    <div className="sh-agenda">
      <div className="sh-agenda__bar card">
        <Icon name="user" size={16} />
        <select className="input sh-agenda__person" value={personId} onChange={(e) => setPersonId(e.target.value)} aria-label={t('Scegli persona')}>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>{e.name}{e.id === meId ? t(' (io)') : ''}</option>
          ))}
        </select>
      </div>

      <div className="sh-agenda__list card">
        <div className="sh-agenda__head">
          <span>{t('Giorno')}</span><span>{t('Turno')}</span><span>{t('Assenza')}</span>
        </div>
        {days.map((d) => {
          const key = iso(d);
          const cell = readShift(byDate[key]);
          const raw = leaveByDate[key];
          // Full-day absences are the turno itself — don't repeat them in the assenza column.
          const leave = raw && (raw.partial || cell?.state !== raw.type) ? raw : undefined;
          const segs = cell?.segments.filter(hasLoc) ?? [];
          const isToday = key === todayIso;
          const weekend = isWeekend(d);
          return (
            <div key={key} className={`sh-agenda__row${isToday ? ' is-today' : ''}${weekend ? ' is-weekend' : ''}`}>
              <div className="sh-agenda__c-day">
                <span className="sh-agenda__dow">{d.toLocaleDateString(loc, { weekday: 'short' })}</span>
                <span className="sh-agenda__num">{d.getDate()}</span>
                <span className="sh-agenda__mon">{d.toLocaleDateString(loc, { month: 'short' })}</span>
              </div>
              <div className="sh-agenda__c-shift">
                {cell?.state ? (
                  <span className="sh-agenda__chip" style={{ background: STATE_YELLOW, color: '#5a4a00' }}>{t(STATES.find((s) => s.key === cell.state)?.label ?? cell.state.toUpperCase())}</span>
                ) : segs.length ? (
                  segs.map((seg, i) => (
                    <span key={i} className="sh-agenda__chip" style={{ background: locColor(seg.loc ?? '') }}>
                      <b>{locLabel(seg.loc)}</b>{seg.range ? <span className="sh-agenda__time">{seg.range}</span> : null}
                    </span>
                  ))
                ) : (
                  <span className="sh-agenda__empty">—</span>
                )}
              </div>
              <div className={`sh-agenda__c-leave${leave ? '' : ' is-empty'}`}>
                {leave ? (
                  <span className="sh-agenda__leave" style={{ background: `${LEAVE_COLOR[leave.type]}18`, color: LEAVE_COLOR[leave.type], border: `1px solid ${LEAVE_COLOR[leave.type]}55` }}>
                    {leave.partial ? `${t(LEAVE_LABEL[leave.type])} ${leave.startTime}–${leave.endTime}` : t(LEAVE_LABEL[leave.type])}
                  </span>
                ) : <span className="sh-agenda__empty">—</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Read-only "chi c'è oggi" board: everyone grouped by the sede they work in. */
function DayRoster({ date, employees, locations }: { date: Date; employees: Employee[]; locations: ShiftLocation[] }) {
  const t = useLang().t;
  const shiftsQ = useShifts();
  const dISO = iso(date);
  const leavesQ = useApprovedLeaves(dISO, dISO);

  const byEmp = useMemo(() => {
    const m: Record<string, Shift> = {};
    for (const s of shiftsQ.data ?? []) if (s.date.slice(0, 10) === dISO) m[s.employeeId] = s;
    return m;
  }, [shiftsQ.data, dISO]);

  // A single-day range means every returned leave covers this day. Full-day
  // absences already appear as the person's turno, so only hourly permits (and
  // days overwritten by hand) still need their own line.
  const leaves = (leavesQ.data ?? []).filter((l) => l.partial || readShift(byEmp[l.employeeId])?.state !== l.type);

  const { byLoc, stateGroups, unassigned } = useMemo(() => {
    const byLoc: Record<string, { name: string; range?: string | null }[]> = {};
    const stateGroups: Record<string, string[]> = {};
    const unassigned: string[] = [];
    // People on approved leave are already listed under "assenze" — keep them out of "senza turno".
    const onLeave = new Set((leavesQ.data ?? []).map((l) => l.employeeId));
    for (const emp of employees) {
      const cell = readShift(byEmp[emp.id]);
      if (cell?.state) { (stateGroups[cell.state] ??= []).push(emp.name); continue; }
      const segs = cell?.segments.filter(hasLoc) ?? [];
      if (segs.length) { for (const seg of segs) (byLoc[seg.loc ?? ''] ??= []).push({ name: emp.name, range: seg.range }); }
      else if (!onLeave.has(emp.id)) unassigned.push(emp.name);
    }
    return { byLoc, stateGroups, unassigned };
  }, [employees, byEmp, leavesQ.data]);

  const workingLocs = locations.filter((l) => byLoc[l.key]?.length);
  const hasAbsences = STATES.some((s) => stateGroups[s.key]?.length) || leaves.length > 0;

  if (employees.length === 0) {
    return <div className="card" style={{ padding: 24 }}><p className="muted" style={{ margin: 0 }}>{t('Nessun dipendente attivo.')}</p></div>;
  }

  return (
    <div className="sh-day">
      {workingLocs.length === 0 && !hasAbsences ? (
        <div className="card" style={{ padding: 24 }}><p className="muted" style={{ margin: 0 }}>{t('Nessun turno assegnato in questa giornata.')}</p></div>
      ) : (
        <div className="sh-day__grid">
          {workingLocs.map((l) => (
            <section key={l.key} className="sh-day__loc card">
              <header className="sh-day__loc-head">
                <span className="sh-day__dot" style={{ background: locColor(l.key) }} />
                <b>{l.short ?? l.key.toUpperCase()}</b>
                <span className="muted sh-day__loc-name">{l.label}</span>
                <span className="sh-day__count">{byLoc[l.key].length}</span>
              </header>
              <ul className="sh-day__people">
                {byLoc[l.key].map((r, i) => (
                  <li key={i}><span className="sh-day__name">{r.name}</span>{r.range && <span className="sh-day__time">{r.range}</span>}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {hasAbsences && (
        <section className="sh-day__abs card">
          <header className="sh-day__abs-head"><Icon name="clock" size={14} /> {t('Riposo & assenze')}</header>
          <div className="sh-day__abs-body">
            {STATES.map((s) => stateGroups[s.key]?.length ? (
              <div key={s.key} className="sh-day__abs-row">
                <span className="sh-day__abs-label" style={{ background: STATE_YELLOW, color: '#5a4a00' }}>{t(s.label)}</span>
                <span>{stateGroups[s.key].join(', ')}</span>
              </div>
            ) : null)}
            {(['ferie', 'permesso', 'malattia'] as const).map((lt) => {
              const rows = leaves.filter((l) => l.type === lt);
              if (!rows.length) return null;
              return (
                <div key={lt} className="sh-day__abs-row">
                  <span className="sh-day__abs-label" style={{ background: `${LEAVE_COLOR[lt]}22`, color: LEAVE_COLOR[lt] }}>{t(LEAVE_LABEL[lt])}</span>
                  <span>{rows.map((l) => `${l.employee?.name ?? ''}${l.partial ? ` (${l.startTime}–${l.endTime})` : ''}`).join(', ')}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {unassigned.length > 0 && (
        <div className="sh-day__none muted">{t('Senza turno:')} {unassigned.join(', ')}</div>
      )}
    </div>
  );
}

function ShiftEditor({ name, date, current, locations, defaultTime, onClose, onSave }: {
  name: string; date: string; current: CellValue; locations: ShiftLocation[]; defaultTime: string;
  onClose: () => void; onSave: (v: CellValue) => void;
}) {
  const { lang, t } = useLang();
  const loc = lang === 'en' ? 'en-GB' : 'it-IT';
  const [tab, setTab] = useState<'work' | 'state'>(current?.state ? 'state' : 'work');
  const [seg1, setSeg1] = useState<ShiftSegment>(current?.segments[0] ?? { loc: locations[0]?.key ?? '', range: defaultTime });
  const [seg2, setSeg2] = useState<ShiftSegment | null>(current?.segments[1] ?? null);

  const save = () => {
    const segs = (seg2 ? [seg1, seg2] : [seg1]).filter((s) => s.loc);
    if (segs.length === 0) { toast.error(t('Scegli almeno una sede.')); return; }
    onSave({ segments: segs });
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 20 }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(460px, 100%)', padding: 18, maxHeight: '88vh', overflow: 'auto' }}>
        <div className="spaced" style={{ marginBottom: 14 }}>
          <div><strong>{name}</strong><div className="muted" style={{ fontSize: 12, textTransform: 'capitalize' }}>{new Date(date).toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long' })}</div></div>
          <button className="btn btn--ghost btn--icon" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="role-switch" style={{ marginBottom: 14 }}>
          <button className={tab === 'work' ? 'is-active' : ''} onClick={() => setTab('work')}>{t('Turno (sede)')}</button>
          <button className={tab === 'state' ? 'is-active' : ''} onClick={() => setTab('state')}>{t('OFF / Assenza')}</button>
        </div>
        {tab === 'work' ? (
          <div className="col" style={{ gap: 14 }}>
            <SegmentEditor label={t('1ª parte')} seg={seg1} setSeg={setSeg1} locations={locations} />
            {seg2 !== null ? (
              <div>
                <SegmentEditor label={t('2ª parte (turno spezzato)')} seg={seg2} setSeg={setSeg2 as (s: ShiftSegment) => void} locations={locations} />
                <button className="btn btn--sm" style={{ marginTop: 6, color: 'var(--danger)' }} onClick={() => setSeg2(null)}><Icon name="x" size={12} /> {t('Rimuovi 2ª parte')}</button>
              </div>
            ) : (
              <button className="btn btn--sm" onClick={() => setSeg2({ loc: locations[1]?.key ?? locations[0]?.key ?? '', range: '' })}>
                <Icon name="plus" size={12} /> {t('Aggiungi 2ª parte (turno spezzato)')}
              </button>
            )}
            <div className="spaced" style={{ borderTop: '1px solid var(--line,#eee)', paddingTop: 12 }}>
              <button className="btn btn--sm" style={{ color: 'var(--danger)' }} onClick={() => onSave(null)}>{t('Svuota giornata')}</button>
              <button className="btn btn--brand" onClick={save}>{t('Salva turno')}</button>
            </div>
          </div>
        ) : (
          <div className="col" style={{ gap: 8 }}>
            {STATES.map((s) => (
              <button key={s.key} onClick={() => onSave({ state: s.key, segments: [] })}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--line,#eee)', background: current?.state === s.key ? STATE_YELLOW : 'var(--bg,#fff)', cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, background: STATE_YELLOW }} /> {t(s.label)}
              </button>
            ))}
            <button className="btn btn--sm" style={{ marginTop: 4, color: 'var(--danger)' }} onClick={() => onSave(null)}><Icon name="x" size={12} /> {t('Svuota giornata')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function SegmentEditor({ label, seg, setSeg, locations }: { label: string; seg: ShiftSegment; setSeg: (s: ShiftSegment) => void; locations: ShiftLocation[] }) {
  const t = useLang().t;
  return (
    <div className="field">
      <label className="label">{label}</label>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <select className="input" style={{ flex: 2, minWidth: 160 }} value={seg.loc ?? ''} onChange={(e) => setSeg({ ...seg, loc: e.target.value })}>
          <option value="">{t('— sede —')}</option>
          {locations.map((l) => <option key={l.key} value={l.key}>{l.short ? `${l.short} · ${l.label}` : l.label}</option>)}
        </select>
        <input className="input" style={{ flex: 1, minWidth: 110 }} type="time" value={seg.range ?? ''} onChange={(e) => setSeg({ ...seg, range: e.target.value })} title={t('Orario inizio')} />
      </div>
    </div>
  );
}
